import { checkIsImage } from "@/common/utils/fileValidator";
import { ImageIcon, UploadIcon } from "lucide-react";
import { FormEventHandler, forwardRef } from "react"
import Spinner from "../Spinner";
import { ErrorMessage } from '../ErrorMessage';

export interface ImageUploaderProps extends React.HTMLAttributes<HTMLInputElement> {
  label: string,
  name: string,
  currentImageUrl?: string,
  isLoading: boolean,
  accept?: string,
  handleChange?: (file: File) => void
  disabled?: boolean
  errorMessage?: string
}

export const ImageUploader = forwardRef<HTMLInputElement, ImageUploaderProps>(({
  label,
  name,
  currentImageUrl,
  isLoading,
  handleChange,
  onChange,
  disabled,
  errorMessage,
  ...props
}, ref) => {

  const handleOnChange: FormEventHandler<HTMLInputElement> = (event) => {
    onChange?.(event)

    if (handleChange) {
      const file = (event.target as HTMLInputElement & { files: FileList }).files?.[0]
      if (!file || !checkIsImage(file)) {
        return
      }

      handleChange(file)
    }
  }

  if (isLoading) {
    return (
      <div className="flex flex-col items-start shrink-0">
        <p className="text-xs mb-1">{label}</p>
        <div className="flex justify-center items-center w-[80px] h-[80px] bg-neutral-1">
          <Spinner className="size-4 fill-white-800" />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-start shrink-0">
      <p className="text-xs mb-1">{label}</p>

      {currentImageUrl
        ? (
          <label className={`relative flex flex-col justify-center items-center ${disabled ? "" : "cursor-pointer"}`} htmlFor={`${disabled ? "" : "file_input"}`}>
            <img src={currentImageUrl} className={`object-cover rounded-full w-[80px] h-[80px] ${disabled ? "" : "hover:bg-white-100"}`} />
            {disabled ? <></> :
              <div className="opacity-0 backdrop-blur-[4px] absolute inset-[-1px] bg-black-600 flex justify-center items-center rounded-full hover:opacity-100">
                <UploadIcon />
              </div>
            }
          </label>
        )
        : (
          <>
            <label className={`flex flex-col justify-center w-[80px] h-[80px] items-center bg-neutral-1 ${disabled ? "" : "cursor-pointer"}`} htmlFor={`${disabled ? "" : "file_input"}`}>
              <div className="border border-neutral-3 rounded-full bg-neutral-2 w-full h-full flex justify-center items-center">
                {disabled ? <ImageIcon /> : <UploadIcon />}
              </div>
            </label>
          </>
        )
      }

      <input disabled={disabled} type="file" ref={ref} name={name} className="hidden" id="file_input" onChange={handleOnChange} {...props} />

      {errorMessage && (
        <div className='mt-1'>
          <ErrorMessage name={name} message={errorMessage} />
        </div>
      )}
    </div>
  )
})
